import React, { useState, useEffect } from 'react';
import { BellIcon, CheckCircleIcon, XCircleIcon } from './ui/icons';
import fcmService from '../services/fcmService';

interface NotificationSettings {
  enabled: boolean;
  emailNotifications: boolean;
  clientUpdates: boolean;
  systemAlerts: boolean;
  taskReminders: boolean;
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
  };
}

const PushNotificationSettings: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<NotificationSettings>({
    enabled: true,
    emailNotifications: true,
    clientUpdates: true,
    systemAlerts: true,
    taskReminders: true,
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00'
    }
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [tokens, setTokens] = useState<any[]>([]);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      checkPushSupport();
      loadTokens();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/notification-settings', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
    setLoading(false);
  };

  const checkPushSupport = () => {
    const supported = fcmService.isSupported();
    setPushSupported(supported);
    // Note: We'll set pushEnabled based on actual FCM tokens from the server in loadTokens()
  };

  const loadTokens = async () => {
    try {
      const response = await fetch('/api/auth/fcm-tokens', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const tokens = data.tokens || [];
        setTokens(tokens);
        // Set pushEnabled based on whether we have any active tokens
        const hasTokens = tokens.length > 0;
        setPushEnabled(hasTokens);
        console.log(`Push notification toggle state: ${hasTokens ? 'ON' : 'OFF'} (${tokens.length} tokens)`);
      }
    } catch (error) {
      console.error('Error loading FCM tokens:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const response = await fetch('/api/auth/notification-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ settings })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    
    setSaving(false);
  };

  const togglePushNotifications = async () => {
    try {
      if (pushEnabled) {
        // Disable push notifications - delete current device token
        await fcmService.deleteToken();
        setMessage({ type: 'success', text: 'Push notifications disabled for this device' });
        // Refresh tokens to update the toggle state
        loadTokens();
      } else {
        // Enable push notifications - subscribe and get token
        const token = await fcmService.subscribeToPushNotifications();
        if (token) {
          setMessage({ type: 'success', text: 'Push notifications enabled for this device' });
          // Refresh tokens to update the toggle state
          loadTokens();
        } else {
          setMessage({ type: 'error', text: 'Failed to enable push notifications' });
        }
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      setMessage({ type: 'error', text: 'Error managing push notifications' });
    }
  };

  const updateSetting = (key: keyof NotificationSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateQuietHours = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        [key]: value
      }
    }));
  };

  const sendTestNotification = async () => {
    setSendingTest(true);
    setMessage(null);
    
    try {
      const response = await fetch('/api/notifications/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const pushResult = data.pushResult;
        
        if (pushResult && pushResult.success) {
          setMessage({ 
            type: 'success', 
            text: `Test notification sent successfully! Push delivered to ${pushResult.successCount} device(s).` 
          });
        } else if (pushResult && !pushResult.success) {
          setMessage({ 
            type: 'error', 
            text: `Test notification saved but push failed: ${pushResult.error || 'Unknown error'}. Check browser console for details.` 
          });
        } else {
          setMessage({ type: 'success', text: 'Test notification sent successfully!' });
        }
        
        // Log detailed results for debugging
        if (pushResult) {
          console.log('Push notification result:', pushResult);
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      setMessage({ type: 'error', text: `Failed to send test notification: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
    
    setSendingTest(false);
  };

  const deleteDevice = async (tokenId: string, deviceInfo: any) => {
    const deviceName = formatDeviceInfo(deviceInfo);
    const confirmed = window.confirm(`Are you sure you want to remove "${deviceName}" from your active devices? You will no longer receive push notifications on this device.`);
    
    if (!confirmed) return;

    try {
      const response = await fetch(`/api/auth/fcm-tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Device removed successfully' });
        // Refresh the tokens list
        loadTokens();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove device');
      }
    } catch (error) {
      console.error('Error removing device:', error);
      setMessage({ type: 'error', text: `Failed to remove device: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const sendTestNotificationToDevice = async (token: string, deviceInfo: any) => {
    const deviceName = formatDeviceInfo(deviceInfo);
    setSendingTest(true);
    
    try {
      const response = await fetch('/api/notifications/test-device', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token,
          deviceInfo,
          message: `🧪 Test notification sent to ${deviceName}`
        })
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `Test notification sent successfully to ${deviceName}` 
        });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification to device:', error);
      setMessage({ 
        type: 'error', 
        text: `Failed to send test notification to ${deviceName}: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    }
    
    setSendingTest(false);
  };

  const formatDeviceInfo = (deviceInfo: any) => {
    if (!deviceInfo) return 'Unknown Device';
    
    // Use detailed device information if available
    if (deviceInfo.deviceModel && deviceInfo.deviceBrand) {
      return `${deviceInfo.deviceBrand} ${deviceInfo.deviceModel}`;
    }
    
    if (deviceInfo.deviceModel) {
      return deviceInfo.deviceModel;
    }
    
    if (deviceInfo.deviceBrand) {
      return deviceInfo.deviceBrand;
    }
    
    // Fallback to platform and user agent for web browsers
    const platform = deviceInfo.platform || '';
    const userAgent = deviceInfo.userAgent || '';
    
    if (platform && platform !== 'Mobile') {
      if (userAgent.includes('Chrome')) return 'Chrome Browser';
      if (userAgent.includes('Firefox')) return 'Firefox Browser';
      if (userAgent.includes('Safari')) return 'Safari Browser';
      return `${platform} Browser`;
    }
    
    if (platform) return platform.replace(/"/g, '');
    if (userAgent.includes('Chrome')) return 'Chrome Browser';
    if (userAgent.includes('Firefox')) return 'Firefox Browser';
    if (userAgent.includes('Safari')) return 'Safari Browser';
    return 'Web Browser';
  };

  const formatLastSeen = (date: string) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BellIcon className="w-6 h-6 text-blue-500" />
              Push Notification Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {message && (
            <div className={`p-3 rounded-md flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                <XCircleIcon className="w-5 h-5" />
              )}
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-gray-600">Loading settings...</p>
            </div>
          ) : (
            <>
              {/* Push Notification Support */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Browser Support</h3>
                <div className="flex items-center gap-3">
                  {pushSupported ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircleIcon className="w-5 h-5" />
                      <span>Push notifications are supported</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircleIcon className="w-5 h-5" />
                      <span>Push notifications are not supported in this browser</span>
                    </div>
                  )}
                </div>
                
                {pushSupported && (
                  <div className="mt-3 space-y-2">
                    <button
                      onClick={togglePushNotifications}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                        pushEnabled
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {pushEnabled ? 'Disable Push Notifications' : 'Enable Push Notifications'}
                    </button>
                    
                    {pushEnabled && (
                      <button
                        onClick={sendTestNotification}
                        disabled={sendingTest}
                        className="ml-2 px-4 py-2 rounded-md text-sm font-medium transition bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingTest ? 'Sending...' : 'Send Test Notification'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Active Devices */}
              {tokens.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Active Devices</h3>
                  <div className="space-y-2">
                    {tokens.map((token, index) => (
                      <div key={token.id || index} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                        <div>
                          <span className="font-medium">{formatDeviceInfo(token.deviceInfo)}</span>
                          <span className="text-gray-500 ml-2">• Last seen: {formatLastSeen(token.deviceInfo?.lastSeen)}</span>
                          {token.deviceInfo?.systemVersion && (
                            <span className="text-gray-400 ml-2">• {token.deviceInfo.systemVersion}</span>
                          )}
                          {token.deviceInfo?.appVersion && (
                            <span className="text-gray-400 ml-2">• v{token.deviceInfo.appVersion}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-green-600 text-xs">Active</span>
                          <button
                            onClick={() => sendTestNotificationToDevice(token.token, token.deviceInfo)}
                            disabled={sendingTest}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                            title="Send test notification to this device"
                          >
                            {sendingTest ? 'Sending...' : 'Test'}
                          </button>
                          <button
                            onClick={() => deleteDevice(token.id, token.deviceInfo)}
                            className="text-red-600 hover:text-red-800 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                            title="Remove this device"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notification Type Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Notification Types</h3>
                
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900">Master Switch</span>
                      <p className="text-sm text-gray-600">Enable or disable all push notifications</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={(e) => updateSetting('enabled', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </label>

                  {settings.enabled && (
                    <>
                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">Email Notifications</span>
                          <p className="text-sm text-gray-600">Get notified when new emails arrive</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications}
                          onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">Client Updates</span>
                          <p className="text-sm text-gray-600">Get notified about client status changes</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.clientUpdates}
                          onChange={(e) => updateSetting('clientUpdates', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">System Alerts</span>
                          <p className="text-sm text-gray-600">Get notified about system-wide alerts</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.systemAlerts}
                          onChange={(e) => updateSetting('systemAlerts', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">Task Reminders</span>
                          <p className="text-sm text-gray-600">Get notified about upcoming tasks and deadlines</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.taskReminders}
                          onChange={(e) => updateSetting('taskReminders', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Quiet Hours */}
              {settings.enabled && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Quiet Hours</h3>
                  
                  <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900">Enable Quiet Hours</span>
                      <p className="text-sm text-gray-600">Disable notifications during specific hours</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.quietHours.enabled}
                      onChange={(e) => updateQuietHours('enabled', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </label>

                  {settings.quietHours.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={settings.quietHours.startTime}
                          onChange={(e) => updateQuietHours('startTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                        <input
                          type="time"
                          value={settings.quietHours.endTime}
                          onChange={(e) => updateQuietHours('endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-lg">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={saveSettings}
              disabled={saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSettings; 